/**
 * Unified Loading Manager
 *
 * Centralizes all loading sources into a single progress tracker.
 * Ensures the custom loader stays in sync with the browser's native loading bar.
 */

// Loading sources with weights (total = 100%) and timeouts
const SOURCES = {
  worker: { weight: 40, timeout: 15000 }, // assetList images via web worker
  three: { weight: 30, timeout: 10000 }, // Three.js textures
  dom: { weight: 20, timeout: 8000 }, // DOM images/videos
  fonts: { weight: 5, timeout: 5000 }, // Web fonts
  browser: { weight: 5, timeout: 12000 }, // window.onload sync
};

// State for each source
const sourceState = {
  worker: { progress: 0, complete: false, timedOut: false },
  three: { progress: 0, complete: false, timedOut: false },
  dom: { progress: 0, complete: false, timedOut: false },
  fonts: { progress: 0, complete: false, timedOut: false },
  browser: { progress: 0, complete: false, timedOut: false },
};

// Listeners for progress updates
const progressListeners = new Set();
const completeListeners = new Set();

let isComplete = false;
let lastEmittedProgress = 0;

/**
 * Calculate weighted progress across all sources
 */
function calculateProgress() {
  let totalWeight = 0;
  let weightedProgress = 0;

  for (const [key, config] of Object.entries(SOURCES)) {
    const state = sourceState[key];
    const effectiveProgress =
      state.complete || state.timedOut ? 100 : state.progress;
    weightedProgress += effectiveProgress * config.weight;
    totalWeight += config.weight;
  }

  return totalWeight > 0 ? weightedProgress / totalWeight : 0;
}

/**
 * Check if all sources are complete or timed out
 */
function checkComplete() {
  return Object.values(sourceState).every((s) => s.complete || s.timedOut);
}

/**
 * Notify all progress listeners
 */
function notifyProgress(progress) {
  // Only emit if progress increased (avoid jitter)
  if (progress <= lastEmittedProgress && progress < 100) return;
  lastEmittedProgress = progress;

  progressListeners.forEach((fn) => {
    try {
      fn(progress);
    } catch (_) {}
  });

  // Note: We don't emit on the global emitter here to avoid circular dependencies
  // The LoaderView subscribes directly to this manager via onProgress()
}

/**
 * Notify all complete listeners
 */
function notifyComplete() {
  if (isComplete) return;
  isComplete = true;

  completeListeners.forEach((fn) => {
    try {
      fn();
    } catch (_) {}
  });

  // Note: We don't emit on the global emitter here to avoid circular dependencies
  // The LoaderView subscribes directly to this manager via onComplete()
}

/**
 * Update progress for a specific source
 */
export function updateSourceProgress(source, progress) {
  if (!sourceState[source] || sourceState[source].complete) return;

  sourceState[source].progress = Math.min(100, Math.max(0, progress));

  const combined = calculateProgress();
  notifyProgress(combined);

  if (progress >= 100) {
    markSourceComplete(source);
  }
}

/**
 * Mark a source as complete
 */
export function markSourceComplete(source) {
  if (!sourceState[source] || sourceState[source].complete) return;

  sourceState[source].progress = 100;
  sourceState[source].complete = true;

  const combined = calculateProgress();
  notifyProgress(combined);

  if (checkComplete()) {
    notifyComplete();
  }
}

/**
 * Mark a source as timed out (treated as complete for progress purposes)
 */
export function markSourceTimedOut(source) {
  if (!sourceState[source] || sourceState[source].complete) return;

  sourceState[source].timedOut = true;

  const combined = calculateProgress();
  notifyProgress(combined);

  if (checkComplete()) {
    notifyComplete();
  }
}

/**
 * Subscribe to progress updates
 */
export function onProgress(fn) {
  progressListeners.add(fn);
  // Immediately call with current progress
  fn(calculateProgress());
  return () => progressListeners.delete(fn);
}

/**
 * Subscribe to completion
 */
export function onComplete(fn) {
  if (isComplete) {
    fn();
    return () => {};
  }
  completeListeners.add(fn);
  return () => completeListeners.delete(fn);
}

/**
 * Get current progress
 */
export function getProgress() {
  return calculateProgress();
}

/**
 * Check if loading is complete
 */
export function isLoadingComplete() {
  return isComplete;
}

/**
 * Get state of a specific source (for debugging)
 */
export function getSourceState(source) {
  return sourceState[source] ? { ...sourceState[source] } : null;
}

/**
 * Get timeout for a source
 */
export function getSourceTimeout(source) {
  return SOURCES[source]?.timeout ?? 10000;
}

/**
 * Start timeout for a source
 */
export function startSourceTimeout(source) {
  const timeout = getSourceTimeout(source);
  return setTimeout(() => {
    if (!sourceState[source]?.complete) {
      console.warn(
        `[LoadingManager] Source "${source}" timed out after ${timeout}ms`
      );
      markSourceTimedOut(source);
    }
  }, timeout);
}

/**
 * Reset loading state (for page transitions or testing)
 */
export function reset() {
  isComplete = false;
  lastEmittedProgress = 0;

  for (const key of Object.keys(sourceState)) {
    sourceState[key] = { progress: 0, complete: false, timedOut: false };
  }

  progressListeners.clear();
  completeListeners.clear();
}

/**
 * Wait for browser's native loading to complete (syncs with browser loading bar)
 */
export function waitForBrowserLoad(maxWaitMs = SOURCES.browser.timeout) {
  if (typeof window === "undefined") {
    markSourceComplete("browser");
    return Promise.resolve();
  }

  if (document.readyState === "complete") {
    markSourceComplete("browser");
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const timeout = setTimeout(() => {
      markSourceTimedOut("browser");
      resolve();
    }, maxWaitMs);

    window.addEventListener(
      "load",
      () => {
        clearTimeout(timeout);
        markSourceComplete("browser");
        resolve();
      },
      { once: true }
    );
  });
}

/**
 * Wait for fonts to load
 */
export function waitForFonts(maxWaitMs = SOURCES.fonts.timeout) {
  if (typeof document === "undefined" || !document.fonts?.ready) {
    markSourceComplete("fonts");
    return Promise.resolve();
  }

  const timeout = startSourceTimeout("fonts");

  return document.fonts.ready
    .then(() => {
      clearTimeout(timeout);
      markSourceComplete("fonts");
    })
    .catch(() => {
      clearTimeout(timeout);
      markSourceTimedOut("fonts");
    });
}

export default {
  updateSourceProgress,
  markSourceComplete,
  markSourceTimedOut,
  onProgress,
  onComplete,
  getProgress,
  isLoadingComplete,
  getSourceState,
  getSourceTimeout,
  startSourceTimeout,
  reset,
  waitForBrowserLoad,
  waitForFonts,
};
